if (!customElements.get('punartha-hero-slider')) {
  customElements.define(
    'punartha-hero-slider',
    class PunarthaHeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-hero-slide]')];
        this.dots = [...this.querySelectorAll('[data-hero-dot]')];
        this.currentLabel = this.querySelector('[data-hero-current]');
        this.liveRegion = this.querySelector('[data-hero-live]');
        this.autoplayButton = this.querySelector('[data-hero-autoplay]');
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.delay = Math.max(4000, Number(this.dataset.delay) || 7000);
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.isAutoplayEnabled = this.dataset.autoplay === 'true' && !window.Shopify?.designMode;
        this.userPaused = this.prefersReducedMotion.matches;

        this.setHeaderOffset(true);
        this.slides.forEach((slide, index) => {
          slide.inert = index !== this.index;
        });
        if (this.slides.length < 2) return;

        this.onPrevious = () => this.show(this.index - 1, true);
        this.onNext = () => this.show(this.index + 1, true);
        this.onAutoplayToggle = () => {
          this.userPaused = !this.userPaused;
          this.userPaused ? this.stop() : this.start();
          this.updateAutoplayButton();
        };
        this.onVisibilityChange = () => (document.hidden ? this.stop() : this.start());
        this.onPointerEnter = () => this.stop();
        this.onPointerLeave = () => this.start();
        this.onFocusIn = () => this.stop();
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.start();
        };
        this.onBlockSelect = (event) => {
          const selectedSlide = event.target.closest?.('[data-hero-slide]');
          if (!selectedSlide || !this.contains(selectedSlide)) return;
          this.stop();
          this.show(Number(selectedSlide.dataset.slideIndex), false);
        };

        this.querySelector('[data-hero-previous]')?.addEventListener('click', this.onPrevious);
        this.querySelector('[data-hero-next]')?.addEventListener('click', this.onNext);
        this.autoplayButton?.addEventListener('click', this.onAutoplayToggle);
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.heroDot), true)));
        this.addEventListener('mouseenter', this.onPointerEnter);
        this.addEventListener('mouseleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.updateAutoplayButton();

        if ('IntersectionObserver' in window) {
          this.visibilityObserver = new IntersectionObserver(
            ([entry]) => {
              this.isVisible = entry.isIntersecting;
              entry.isIntersecting ? this.start() : this.stop();
            },
            { threshold: 0.2 }
          );
          this.visibilityObserver.observe(this);
        } else {
          this.isVisible = true;
          this.start();
        }

        this.headerSections = [...document.querySelectorAll('.shopify-section-group-header-group')];
        if ('ResizeObserver' in window) {
          this.headerObserver = new ResizeObserver(() => {
            const compactHeader = document.querySelector('.section-header.scrolled-past-header');
            if (!compactHeader) this.setHeaderOffset();
          });
          this.headerSections.forEach((element) => this.headerObserver.observe(element));
        }
        this.onViewportResize = () => {
          window.clearTimeout(this.headerResizeTimer);
          this.headerResizeTimer = window.setTimeout(() => {
            const compactHeader = document.querySelector('.section-header.scrolled-past-header');
            if (!compactHeader) this.setHeaderOffset(true);
          }, 120);
        };
        window.addEventListener('resize', this.onViewportResize, { passive: true });
      }

      disconnectedCallback() {
        this.stop();
        this.visibilityObserver?.disconnect();
        this.headerObserver?.disconnect();
        window.removeEventListener('resize', this.onViewportResize);
        window.clearTimeout(this.headerResizeTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      setHeaderOffset(force = false) {
        const headerSections = [...document.querySelectorAll('.shopify-section-group-header-group')];
        const offset = headerSections.reduce((total, element) => total + element.getBoundingClientRect().height, 0);
        if (!force && this.headerOffset !== undefined && offset <= this.headerOffset) return;
        this.headerOffset = offset;
        this.style.setProperty('--hero-header-offset', `${offset}px`);
      }

      updateAutoplayButton() {
        if (!this.autoplayButton) return;
        this.autoplayButton.setAttribute('aria-pressed', String(this.userPaused));
        this.autoplayButton.setAttribute('aria-label', this.userPaused ? 'Play hero slideshow' : 'Pause hero slideshow');
        const pauseIcon = this.autoplayButton.querySelector('[data-hero-pause-icon]');
        const playIcon = this.autoplayButton.querySelector('[data-hero-play-icon]');
        if (pauseIcon) pauseIcon.hidden = this.userPaused;
        if (playIcon) playIcon.hidden = !this.userPaused;
      }

      show(nextIndex, announce = false) {
        this.index = (nextIndex + this.slides.length) % this.slides.length;
        this.slides.forEach((slide, index) => {
          const isActive = index === this.index;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
          slide.inert = !isActive;
        });
        this.dots.forEach((dot, index) => {
          const isActive = index === this.index;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', String(isActive));
        });
        if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
        if (announce && this.liveRegion) this.liveRegion.textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
        if (announce) this.restart();
      }

      start() {
        if (document.documentElement.classList.contains('pta-launch-active')) return;
        if (!this.isAutoplayEnabled || this.userPaused || !this.isVisible || document.hidden || this.prefersReducedMotion.matches) return;
        if (!this.timer) this.timer = window.setInterval(() => this.show(this.index + 1), this.delay);
      }

      stop() {
        window.clearInterval(this.timer);
        this.timer = null;
      }

      restart() {
        this.stop();
        this.start();
      }
    }
  );
}
